import { Button, Stack, Text, useToast } from '@pairy/pds-react';
import { useEffect, useState, useRef } from 'react';
import { useScrollEndDetection } from '@pairy/react-hooks';
import * as Portal from '@radix-ui/react-portal';
import { CtaForm, FormRecord, SelectOption } from '@pairy/sheard/types';
import { useAtom, useAtomValue } from 'jotai';

import { useViewerRoute } from '../../../../../contexts/ViewerRoute';
import MobileFormFreeBadge from '../../../../@common/FormFreeBadge/Mobile';
import usePostFormEmailAuth from '../../../../../hooks/mutation/cta/useFormEmailAuth';
import useVerifyFormEmailAuth from '../../../../../hooks/mutation/cta/useVerifyFormEmailAuth';
import { currentPageAtom } from '../../../../../store/index.atom';
import { GOOGLE_EVENT_ID } from '../../../../../constant/googleEventId';
import { isPreviewFormSubmittedAtom } from '../../../../BulkPdfPreview/index.atom';
import { FORM_VALIDATIONS } from '../../../../../constant/formValidations';
import usePostCtaFormWithInvalidation from '../../../../../hooks/mutation/cta/usePostCtaForm';
import CtaFormModalContents from '../../../CtaFormModalContents';

import * as Styled from './index.styles';

interface Props {
  ctaForm?: CtaForm;
  isEmailVerificationEnabled: boolean;
  isPreview: boolean;
  closeModal: (isMutateSuccess?: boolean) => void;
  translateKoToEn: (item: string) => string;
  userSubscriptionPlan: string;
}

const CtaFormModal = ({
  ctaForm,
  isEmailVerificationEnabled,
  isPreview,
  closeModal,
  translateKoToEn,
  userSubscriptionPlan,
}: Props) => {
  const currentPage = useAtomValue(currentPageAtom);
  const [formFields, setFormFields] = useState<FormRecord[]>([]);
  const { historyId } = useViewerRoute();
  const scrollRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerified, setIsVerified] = useState(false);

  const [isPreviewFormSubmitted, setPreviewFormSubmitted] = useAtom(
    isPreviewFormSubmittedAtom,
  );

  if (isPreviewFormSubmitted) {
    return null;
  }

  const isDisabledCloseButton =
    ctaForm?.blind === 'startFrom' && currentPage === 1;

  const { mutate: postCtaFormMutate } = usePostCtaFormWithInvalidation();
  const { mutate: sendAuthEmail } = usePostFormEmailAuth({
    onSuccess: () => {
      setIsCodeSent(true);
      toast(`${translateKoToEn('verification-code-sent')}`);
    },
    onError: () => {
      toast(`${translateKoToEn('verification-code-failed')}`);
    },
  });
  const { mutate: verifyEmail } = useVerifyFormEmailAuth({
    onSuccess: () => {
      setIsVerified(true);
      toast(`${translateKoToEn('verification-success')}`);
    },
    onError: () => {
      setIsVerified(false);
      toast(`${translateKoToEn('verification-failed')}`);
    },
  });
  const { handleScroll } = useScrollEndDetection(scrollRef);

  const handleSubmit = () => {
    if (typeof historyId === 'string') {
      postCtaFormMutate({
        historyId: historyId,
        formRecord: formFields,
        code: verificationCode === '' ? undefined : verificationCode,
        onSuccess: () => {
          closeModal(true);
          toast(`${translateKoToEn('form-submit')}`);
        },
      });
    }
  };

  const handleChangeWithId = (id: number, value: FormRecord['value']) => {
    setFormFields((prev) =>
      prev?.map((field) => {
        if (field.id === id) {
          return {
            ...field,
            value,
          };
        }
        return field;
      }),
    );
  };

  useEffect(() => {
    if (ctaForm?.fields) {
      setFormFields(
        ctaForm?.fields.map((field) => {
          if (field.type === 'email') {
            return {
              id: field.id,
              name: field.name,
              type: field.type,
              value: field.initialValue ?? null,
            };
          }
          return {
            id: field.id,
            name: field.name,
            type: field.type,
            value: null,
          };
        }),
      );
    }
  }, []);

  const buttonDisabled = ctaForm?.fields.some((field) => {
    if (field.isRequired) {
      const currentValue = formFields?.find((f) => f.id === field.id)?.value;

      if (!currentValue) {
        return true;
      }

      if (field.type === 'number') {
        const numberValue = currentValue as number;
        return !(
          numberValue <= field.range.max && numberValue >= field.range.min
        );
      }

      if (field.type === 'select') {
        const selectedValue = currentValue as SelectOption[];
        return !(
          selectedValue.length >= field.range.min &&
          selectedValue.length <= field.range.max
        );
      }

      if (field.type === 'email') {
        const emailValue = currentValue as string;

        const isInvalidEmail =
          !FORM_VALIDATIONS.email.pattern.value.test(emailValue as string) &&
          Boolean(emailValue);

        return isInvalidEmail || (isEmailVerificationEnabled && !isVerified);
      }

      if (field.type === 'string' || field.type === 'name') {
        const stringValue = currentValue as string;
        return !stringValue || stringValue.trim() === '';
      }

      return !currentValue;
    }
  });

  const handleSendCode = () => {
    if (isPreview) {
      setIsCodeSent(true);
      toast(`미리보기 모드입니다. 아무 코드나 입력하셔도 인증이 완료됩니다.`);
      return;
    }
    sendAuthEmail({
      email: formFields.find((f) => f.type === 'email')?.value as string,
      historyId: historyId as string,
    });
  };

  const handleVerify = () => {
    if (isPreview) {
      setIsVerified(true);
      toast(`${translateKoToEn('verification-success')}`);
      return;
    }

    verifyEmail({
      email: formFields.find((f) => f.type === 'email')?.value as string,
      code: verificationCode,
      historyId: historyId as string,
    });
  };

  return (
    <>
      <Styled.Modal direction="column" gap={16} justifyContent="center">
        <Styled.Description
          variant="lg-semibold"
          color="white"
          textAlign="center"
        >
          {ctaForm?.description}
        </Styled.Description>
        <Styled.ModalBody direction="column" gap={24}>
          <Stack
            justifyContent="space-between"
            alignItems="flex-start"
            gap={16}
          >
            <Text variant="lg-semibold" color="black">
              {ctaForm?.name}
            </Text>
            {!isDisabledCloseButton && (
              <Styled.CloseButton
                type="button"
                onClick={() => {
                  closeModal();
                }}
              >
                <Text variant="md-medium" color="zinc_400">
                  닫기
                </Text>
              </Styled.CloseButton>
            )}
          </Stack>
          <Styled.ModalContents
            direction="column"
            gap={16}
            onScroll={handleScroll}
            padding={{ bottom: 106 }}
            ref={scrollRef}
          >
            <CtaFormModalContents
              formFields={formFields}
              handleChangeWithId={handleChangeWithId}
              fields={ctaForm?.fields}
              translateKoToEn={translateKoToEn}
              isCodeSent={isCodeSent}
              isVerified={isVerified}
              verificationCode={verificationCode}
              onSendVerificationCode={handleSendCode}
              onVerifyCode={handleVerify}
              onChangeVerificationCode={setVerificationCode}
              isEmailVerificationEnabled={isEmailVerificationEnabled}
            />
          </Styled.ModalContents>

          <Styled.FlexFixedBottomCTAButtonWrapper
            direction="column"
            alignItems="stretch"
            padding={{
              x: 32,
              y: 30.5,
            }}
          >
            <Button
              id={GOOGLE_EVENT_ID.form}
              variant="black_fill"
              radius={9999}
              paddingY={12}
              textVariant="lg-semibold"
              disabled={buttonDisabled}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();

                if (isPreview) {
                  closeModal();
                  setPreviewFormSubmitted(true);
                  return;
                }

                handleSubmit();
              }}
            >
              {ctaForm?.button}
            </Button>
            <MobileFormFreeBadge userSubscriptionPlan={userSubscriptionPlan} />
          </Styled.FlexFixedBottomCTAButtonWrapper>
        </Styled.ModalBody>
      </Styled.Modal>
      <Portal.Root>
        <Styled.Dim />
      </Portal.Root>
    </>
  );
};

export default CtaFormModal;
